/**
 * Utility functions for generating audience display names with dynamic suffixes
 * based on targeting settings (placement, age, gender)
 */

import { accountAudienceDisplayName } from '@/utils/account-audiences'

/**
 * Format gender targets into display text
 * @param {string[]} genderTargets - Array of selected genders ['male', 'female']
 * @returns {string} Formatted gender text
 */
export const formatGenderText = (genderTargets) => {
    if (!genderTargets || genderTargets.length === 0 || genderTargets.length === 2) {
        return 'All-Genders'
    }

    if (genderTargets.length === 1) {
        return genderTargets[0].charAt(0).toUpperCase() + genderTargets[0].slice(1)
    }

    return 'All-Genders'
}

/**
 * Format age targets into display text
 * @param {string[]} ageTargets - Array of selected age ranges
 * @returns {string} Formatted age text
 */
export const formatAgeText = (ageTargets) => {
    if (!ageTargets || ageTargets.length === 0) {
        return '18-65'
    }

    const allAges = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+']

    // If all ages are selected
    if (ageTargets.length === allAges.length) {
        return '18-65+'
    }

    // If only one age range is selected
    if (ageTargets.length === 1) {
        return ageTargets[0]
    }

    // Get the range from first to last selected age
    const ageIndices = ageTargets
        .map((age) => allAges.indexOf(age))
        .filter((idx) => idx !== -1)
        .sort((a, b) => a - b)

    if (ageIndices.length > 0) {
        const minIdx = ageIndices[0]
        const maxIdx = ageIndices[ageIndices.length - 1]

        // Check if ages are continuous
        const isContinuous = ageIndices.every((idx, i) => i === 0 || idx === ageIndices[i - 1] + 1)

        if (isContinuous) {
            const startAge = allAges[minIdx].split('-')[0]
            const endAge = allAges[maxIdx].includes('+') ? '65+' : allAges[maxIdx].split('-')[1]
            return `${startAge}-${endAge}`
        }
    }

    // If not continuous or other edge cases, return default
    return '18-65'
}

/**
 * Format placement targets into display text
 * @param {string[]} placementTargets - Array of selected placements
 * @returns {string} Formatted placement text
 */
export const formatPlacementText = (placementTargets) => {
    const ALL_PLACEMENTS_COUNT = 23

    // Only return 'Auto-Placement' when all 23 placements are explicitly selected
    if (placementTargets && (placementTargets.length === ALL_PLACEMENTS_COUNT || placementTargets.length === 0)) {
        return 'Auto-Placement'
    }

    // Return 'Custom-Placement' for empty arrays or partial selections
    return 'Custom-Placement'
}

/**
 * Generate the dynamic suffix for audience names
 * @param {Object} params - Parameters for suffix generation
 * @param {string[]} params.genderTargets - Selected genders
 * @param {string[]} params.ageTargets - Selected age ranges
 * @param {string[]} params.placementTargets - Selected placements
 * @returns {string} Generated suffix
 */
export const generateAudienceSuffix = ({ genderTargets, ageTargets, placementTargets }) => {
    const placementText = formatPlacementText(placementTargets)
    const ageText = formatAgeText(ageTargets)
    const genderText = formatGenderText(genderTargets)

    return `${placementText} - ${ageText} - ${genderText}`
}

/**
 * Generate the full audience display name with dynamic suffix
 * @param {Object} params - Parameters for name generation
 * @param {string} params.stageId - The funnel stage ID
 * @param {string} params.audienceId - The audience ID
 * @param {string} params.defaultName - The default audience name
 * @param {Object} params.reviewLaunchData - Review launch data from wizard
 * @param {Function} params.getStepContentData - Function to get wizard step data
 * @param {Object[]} params.allStages - All available stages
 * @param {Object} params.connectedPublishers - Connected publishers data
 * @param {Function} params.getAudienceGenderTargets - Function to get gender targets
 * @param {Function} params.getAudienceAgeTargets - Function to get age targets
 * @param {Function} params.getAudiencePlatformPlacementTargets - Function to get placement targets
 * @returns {string} The generated display name
 */
export const generateAudienceDisplayName = ({
    stageId,
    audienceId,
    defaultName,
    reviewLaunchData,
    getStepContentData,
    allStages,
    connectedPublishers,
    getAudienceGenderTargets,
    getAudienceAgeTargets,
    getAudiencePlatformPlacementTargets,
}) => {
    const key = `${stageId}_${audienceId}`

    // Names of audiences from the ad account are the customer's own and can be long
    const audienceName = accountAudienceDisplayName(audienceId, defaultName)

    // Check if user has already set a custom name
    if (reviewLaunchData.audienceNames[key]) {
        return reviewLaunchData.audienceNames[key]
    }

    // Check if this is the first time visiting the step
    const rawReviewLaunchData = getStepContentData('reviewLaunch')
    if (!rawReviewLaunchData || !reviewLaunchData.audienceNames[key]) {
        // Auto-generate name using dynamic template based on actual settings
        const stage = allStages.find((s) => s.id === stageId)
        const stageName = stage?.name || 'Unknown Stage'

        // Get name stamp from connected publishers (use first available)
        const firstPublisher = Object.values(connectedPublishers || {})[0]
        const nameStamp = firstPublisher?.label || 'Turba Media'

        // Get actual targeting settings for this audience
        const genderTargets = getAudienceGenderTargets(stageId, audienceId)
        const ageTargets = getAudienceAgeTargets(stageId, audienceId)
        const placementTargets = getAudiencePlatformPlacementTargets(stageId, audienceId)

        // Generate dynamic suffix
        const dynamicSuffix = generateAudienceSuffix({
            genderTargets,
            ageTargets,
            placementTargets,
        })

        // Generate the full name using dynamic suffix
        const autoGeneratedName = `${nameStamp} - ${audienceName} - ${stageName} - ${dynamicSuffix}`

        return autoGeneratedName
    }

    // If not first time and no name set, return the default name
    return audienceName
}
